import { matchPath } from 'react-router-dom';
import { DashboardUserRole } from '../common/enums/DashboardUserRole';

const { User, Admin, SuperAdmin, Sheikh } = DashboardUserRole;

const STAFF = [User, Admin, SuperAdmin];
const EDITORS = [Admin, SuperAdmin];
const ISTIFTA = [Sheikh, SuperAdmin];
const SUPER = [SuperAdmin];

// Keeps the literal paths as the map's keys (for PagePath) while checking every value is a role list.
const definePages = <T extends Record<string, readonly DashboardUserRole[]>>(
  pages: T
) => pages;

/**
 * Which roles may open each page (architecture.md, EP-dashboard-ops-coverage). The route guards in
 * App.tsx and the sidebar both read this map, so a page cannot be hidden in the menu yet reachable by
 * URL. The backend role checks are the authority; this map must never allow a page whose writes the
 * backend refuses for that role.
 */
export const PAGE_ROLES = definePages({
  '/': STAFF,
  '/week-log-analytics': STAFF,
  '/questionnaire/:questionnaireId/analytics': STAFF,
  '/technical-support': STAFF,
  '/technical-support/:id': STAFF,
  '/content': EDITORS,
  '/content-context': EDITORS,
  '/questions': EDITORS,
  '/answers': EDITORS,
  '/media': EDITORS,
  '/questionnaire': EDITORS,
  '/istifta': ISTIFTA,
  '/istifta/links': ISTIFTA,
  '/istifta/:id': ISTIFTA,
  '/user-list': SUPER,
  '/versions-management': SUPER,
  '/notification-configuration': SUPER,
  '/broadcast': SUPER,
  '/broadcast/create': SUPER,
  '/broadcast/:id/stats': SUPER,
  '/sessions': SUPER,
});

export type PagePath = keyof typeof PAGE_ROLES;

const PATTERNS = Object.keys(PAGE_ROLES) as PagePath[];

/** The matrix entry for a concrete URL path, or undefined when no page matches. */
export const pageFor = (pathname: string): PagePath | undefined =>
  // Literal segments win over params (react-router ranks them the same way), e.g. /istifta/links
  // before /istifta/:id.
  PATTERNS.filter((pattern) => matchPath(pattern, pathname)).sort(
    (a, b) => (a.includes(':') ? 1 : 0) - (b.includes(':') ? 1 : 0)
  )[0];

/** Whether a role may open the page at this URL path. */
export const canOpen = (
  role: DashboardUserRole | undefined,
  pathname: string
) => {
  const page = pageFor(pathname);
  if (!role || !page) return false;
  return PAGE_ROLES[page].includes(role);
};

/** Where a role lands when it opens the dashboard root. */
export const homePath = (role: DashboardUserRole | undefined) =>
  role === Sheikh ? '/istifta' : '/';
